import { Data, Validations } from "../../types/shared/Validation";
import { PersonalDetails, ReferralDetails, TermsConditions, TravelDetails, TravelPlan, FeedbackDetails } from "../../types/travel/Policy";
import { getDaysBetweenDates, isDateBefore, today } from "../../utils/date";
import { differenceInMonths, differenceInYears, isDate } from "date-fns";
import { EMAIL_REGEX } from "../../constants";

export const LETTERS_AND_NUMBERS_REGEX = /^[A-Za-z0-9]+$/;
const isValidDate = (date: unknown): date is Date => isDate(date);

const emailValidation = {
  required: true,
  pattern: {
    regex: EMAIL_REGEX,
    message: "Please check email format",
  },
};

const fullNameValidation = {
  required: true,
  length: {
    maxLength: 60,
    message: "Text exceeds max. length",
  },
};

const identificationNumberStatusValidation = {
  required: true,
  pattern: {
    regex: /^valid$/,
    message: "Has to be valid",
  },
};

// const validateDateNotInPast = (value: Date) => {
//   if (value.getTime() < today().getTime()) {
//     return `Must be in the future`;
//   }

//   return null;
// };

const validateDateNotInFuture = (date: Date) => {
  if (date.getTime() > today().getTime()) {
    return `Must be in the past`;
  }

  return null;
};

const insuredDOBValidation = {
  required: true,
  custom: {
    validate: (date: unknown, personalDetails: Data) => validateDOB(date, personalDetails, { isTraveller: false, isChild: false }),
  },
};

const validateDOB = (date: unknown, personalDetails: Data, detail: { isTraveller: boolean; isChild: boolean }) => {
  if (!isValidDate(date)) return "Incorrect date format";

  let error: string | null = null;
  error = error || validateDateNotInFuture(date);
  error = error || validateAge(date, personalDetails as PersonalDetails, { ...detail, type: "min" });
  error = error || validateAge(date, personalDetails as PersonalDetails, { ...detail, type: "max" });
  return error;
};

const dateOfBirthValidation = {
  required: true,
  custom: {
    validate: (date: unknown, personalDetails: Data) => validateDOB(date, personalDetails, { isTraveller: true, isChild: false }),
  },
};

const childDateOfBirthValidation = {
  required: true,
  custom: {
    validate: (date: unknown, personalDetails: Data) => validateDOB(date, personalDetails, { isTraveller: true, isChild: true }),
  },
};

export const referralDetailsValidations: Validations<ReferralDetails> = {
  fields: {
    name: {
      required: true,
    },
    email: emailValidation,
  },
};

export const feedbackValidations: Validations<FeedbackDetails> = {
  fields: {
    isEasy: {
      required: true,
    },
    isUseful: {
      required: true,
    },
    scale: {
      required: true,
    },
  },
};

export const identificationNumberValidation = {
  required: true,
  custom: {
    validate: (identificationNumber: unknown, personalDetails: Data) => {
      let error: string | null = null;
      error = error || validateOnlyLettersAndNumbers(identificationNumber as string);
      error = error || validateUniqueIdentificationNumber(identificationNumber as string, personalDetails as PersonalDetails);
      return error;
    },
  },
};

const getCalculatedValueForFinValidation = (inputText: string) => {
  const weightageTable = [2, 7, 6, 5, 4, 3, 2];
  let calculatedValue = 0;
  for (let i = 1; i < 8; i++) {
    calculatedValue += parseInt(inputText[i]) * weightageTable[i - 1];
  }
  return calculatedValue;
};

export const validateFinId = (inputText: string) => {
  const regex = /^[S,T,F,G,M]{1}\d{7}/g;
  let error = false;
  if (regex.test(inputText)) {
    const lastLetterList = {
      S: {
        additionValue: 0,
        list: ["A", "B", "C", "D", "E", "F", "G", "H", "I", "Z", "J"],
      },
      T: {
        additionValue: 4,
        list: ["A", "B", "C", "D", "E", "F", "G", "H", "I", "Z", "J"],
      },
      G: {
        additionValue: 4,
        list: ["K", "L", "M", "N", "P", "Q", "R", "T", "U", "W", "X"],
      },
      F: {
        additionValue: 0,
        list: ["K", "L", "M", "N", "P", "Q", "R", "T", "U", "W", "X"],
      },
      M: {
        additionValue: 3,
        list: ["K", "L", "J", "N", "P", "Q", "R", "T", "U", "W", "X"],
      },
    };
    // Calculate the weighted sum
    let calculatedValue = getCalculatedValueForFinValidation(inputText);
    // Add sum based on first letter
    calculatedValue += lastLetterList[inputText[0] as keyof typeof lastLetterList].additionValue;
    //calculate the remainder
    calculatedValue %= 11;
    //get the last letter digit
    calculatedValue = 11 - (calculatedValue + 1);
    error = inputText[inputText.length - 1] === lastLetterList[inputText[0] as keyof typeof lastLetterList].list[calculatedValue];
  }
  return error;
};

const travellerArrayValidation = {
  fullName: fullNameValidation,
  identificationNumber: identificationNumberValidation,
  identificationNumberStatus: identificationNumberStatusValidation,
};

export const personalDetailsValidations: Validations<PersonalDetails> = {
  fields: {
    requester: {
      nested: {
        ...travellerArrayValidation,
        phoneNumber: {
          required: true,
          pattern: {
            regex: /^[0-9]{8}$/,
            message: "Must be 8 digits",
          },
        },
        email: emailValidation,
        dateOfBirth: insuredDOBValidation,
      },
    },
    travellers: {
      array: {
        ...travellerArrayValidation,
        dateOfBirth: dateOfBirthValidation,
      },
    },
    childTravellers: {
      array: {
        ...travellerArrayValidation,
        dateOfBirth: childDateOfBirthValidation,
      },
    },
  },
};

export const planValidations: Validations<TravelPlan> = {
  fields: {
    planName: {
      required: {
        message: "Please select a plan",
      },
    },
  },
};

const isValidNumber = (value: unknown): value is number => !isNaN(value as number);
const validTraveller = (travellerDetail: any, check: string) => {
  const { adultCount, childrenCount, minTraveler, isSingleIndividual } = travellerDetail;
  const checkValue = {
    more: {
      message: isSingleIndividual ? "Please enter at least one individual." : `Must be ${minTraveler} or more individuals.`,
      condition: adultCount + childrenCount < minTraveler,
    },
    less: {
      message: `Must be ${minTraveler} or less individuals.`,
      condition: adultCount + childrenCount > minTraveler,
    },
  };
  if (checkValue[check as keyof typeof checkValue].condition) {
    return checkValue[check as keyof typeof checkValue].message;
  }

  return null;
};

const checkAdultCountMore = (travelDetails: { adultCount: unknown; childrenCount: unknown; isSingleIndividual: boolean }, error: any) => {
  const { adultCount, childrenCount, isSingleIndividual } = travelDetails;
  return error || validTraveller({ adultCount, childrenCount, minTraveler: isSingleIndividual ? 1 : 2, isSingleIndividual }, "more");
};

const adultCountValidation = (travelDetails: { adultCount: unknown; childrenCount: unknown; isSingleIndividual: boolean }, error: any) => {
  const { adultCount, childrenCount, isSingleIndividual } = travelDetails;
  if (isSingleIndividual) {
    error = error || validTraveller({ adultCount, childrenCount, minTraveler: 10, isSingleIndividual }, "less");
  }
  error = checkAdultCountMore({ adultCount, childrenCount, isSingleIndividual }, error);
  return error;
};

export const detailsValidations: Validations<TravelDetails> = {
  fields: {
    policyType: {
      required: true,
    },
    groupType: {
      required: true,
    },
    adultCount: {
      validateIf: (_, { groupType, policyType }) => {
        return !(groupType === "individual" && policyType === "annual-trip");
      },
      required: true,
      custom: {
        validate: (adultCount, { childrenCount, groupType, policyType }) => {
          const isSingleIndividual = groupType === "individual" && policyType === "single-trip";
          if (!isValidNumber(adultCount)) return null;
          const error: string | null = null;

          if (!isValidNumber(childrenCount)) return error;
          return adultCountValidation({ adultCount, childrenCount, isSingleIndividual }, error);
        },
      },
    },
    childrenCount: {
      validateIf: (_, { groupType, policyType }) => {
        return groupType !== "family" && policyType === "single-trip";
      },
      required: false,
      custom: {
        validate: (childrenCount, { adultCount, groupType, policyType }) => {
          const isSingleIndividual = groupType === "individual" && policyType === "single-trip";
          let error: string | null = null;
          error = error || validTraveller({ adultCount, childrenCount, minTraveler: 10, isSingleIndividual }, "less");
          error = error || validTraveller({ adultCount, childrenCount, minTraveler: isSingleIndividual ? 1 : 2, isSingleIndividual }, "more");
          return error;
        },
      },
    },
    startDate: {
      required: true,
      custom: {
        validate: (startDate: unknown) => {
          if (!isValidDate(startDate)) return "Incorrect start date format";
          let error: string | null = null;
          error = error || validateMaxDaysFromToday(startDate, 180);
          return error;
        },
      },
    },
    endDate: {
      validateIf: (_, { policyType }) => {
        return policyType !== "annual-trip";
      },
      required: true,
      custom: {
        validate: (endDate, { startDate }) => {
          if (!isValidDate(endDate)) return null;
          let error: string | null = null;

          if (!isValidDate(startDate)) return error;

          error = error || validateReturnDateAfterStartDate(startDate, endDate);
          error = error || validateDuration(startDate, endDate, 182);
          return error;
        },
      },
    },
    location: {
      required: true,
    },
  },
};

export const termsValidations: Validations<TermsConditions> = {
  fields: {
    accepted: {
      required: {
        message: "Please agree to proceed",
      },
    },
  },
};
const getValidAge = (date: Date, personalDetails: PersonalDetails, detail: { isTraveller: boolean; isChild: boolean }) => {
  let age = 18;
  let error = "years";
  const calculatedAge = calculateAge(date);
  let calculateMAge = calculatedAge;
  let MaxAge = 80;
  if (detail.isChild && detail.isTraveller) {
    age = 6;
    MaxAge = 17;
    error = "months";
    calculateMAge = calculateMinAge(date);
  }
  return { age, MaxAge, calculatedAge, calculateMAge, error };
};

const validateAge = (date: Date, personalDetails: PersonalDetails, dataType: { type: "max" | "min"; isTraveller: boolean; isChild: boolean }) => {
  const { age, MaxAge, calculatedAge, calculateMAge, error } = getValidAge(date, personalDetails, {
    isTraveller: dataType.isTraveller,
    isChild: dataType.isChild,
  });
  switch (dataType.type) {
    case "max":
      return checkMaxAge(calculatedAge, MaxAge);
    case "min":
      return checkMinAge(calculateMAge, age, error);
  }
};

const checkMaxAge = (inputAge: number, maxAge: number) => {
  return inputAge > maxAge ? `Maximum age is ${maxAge} years` : null;
};

const checkMinAge = (inputAge: number, minAge: number, error: string) => {
  return inputAge < minAge ? `Minimum age is ${minAge} ${error}` : null;
};

const calculateAge = (date: Date): number => {
  return differenceInYears(new Date(), date);
};

const calculateMinAge = (date: Date): number => {
  return differenceInMonths(new Date(), date);
};

const validateMaxDaysFromToday = (date: Date, days: number) => {
  const maxDate = today();
  maxDate.setDate(maxDate.getDate() + days - 1);

  if (date && date.getTime() > maxDate.getTime()) {
    return `Must be within ${days} days from today`;
  }

  return null;
};

const validateDuration = (startDateString: Date, endDateString: Date, maxDays: number) => {
  const days = getDaysBetweenDates(startDateString, endDateString);

  // daysDiff + 1 because if you leave today and return today, the duration is 1 day (not 0)
  if (days !== null && days + 1 > maxDays) {
    return `Duration exceeds ${maxDays} days`;
  }

  return null;
};

const validateReturnDateAfterStartDate = (startDate: Date, endDate: Date) => {
  if (isDateBefore(endDate, startDate)) {
    return "Must be after departure date";
  }

  return null;
};

const validateOnlyLettersAndNumbers = (value: string) => {
  if (!value.match(LETTERS_AND_NUMBERS_REGEX)) {
    return "Only letters and numbers are allowed";
  }
  if (!validateFinId(value)) {
    return "Invalid NRIC/FIN";
  }

  return null;
};

const getNumberOfOccurrences = (values: string[], value: string) => {
  return values.reduce((count, v) => (v === value ? count + 1 : count), 0);
};

const getTravellersId = (personalDetails: PersonalDetails) => {
  const travellersId = [];
  let i = personalDetails.requestingPersonIsTravelingPerson ? 1 : 0;
  const travellers = [...personalDetails.travellers, ...personalDetails.childTravellers];
  for (i; i <= travellers.length; i++) {
    travellersId.push(travellers?.[i]?.identificationNumber);
  }
  const ids: string[] = [personalDetails.requester.identificationNumber, ...travellersId];
  return ids;
};

const validateUniqueIdentificationNumber = (identificationNumber: string, personalDetails: PersonalDetails) => {
  const ids = getTravellersId(personalDetails);
  let error = null;
  if (identificationNumber !== "" && identificationNumber !== undefined && getNumberOfOccurrences(ids, identificationNumber) > 1) {
    error = "Duplicated NRIC/FIN";
  }

  return error;
};
